import React, { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { TextField, Autocomplete, Grid } from '@mui/material';
import HighlightMask from '@/components/HighlightMask/HighlightMask';
import Button from '@/components/UI/Button/Button';
import { errorTypes, fieldTypes } from '@/helpers/constants';
import useApiRequest from '@/hooks/useApiRequest';
import useAppLanguage from '@/hooks/useAppLanguage';
import useError from '@/hooks/useError';
import useToastContext from '@/hooks/useToastContext';
import { setSubCampaignFeedback } from '@/redux/SubCampaign/subCampaign.action';
import { getSubCampaignFeedback, getSubCampaignOriginalValues } from '@/redux/SubCampaign/subCampaign.reducer';
import campaignServices from '@/services/campaignServices';
import { FeedbackCustomForm, FeedbackCustomFormOption, OriginalValues, ReduxFormsState, SubCampaignFeedback } from '@/types';
import { isValidCampaignNumberFormat, openINTAKTWindow } from './feedback-utils';
import TitleWithDate from './TitleWithDate';

const ADDITIONAL_INFORMATION_SECTION = 'additionalInformationAboutTheCase' as const;
const FEEDBACK_SECTION = 'feedback' as const;
const CAMPAIGN_STATUS_FIELD = 'campaignStatus' as const;
const CLASSIFICATION_FIELD = 'classification' as const;
const CAMPAIGN_STATUS_PATH = `${ADDITIONAL_INFORMATION_SECTION}.${CAMPAIGN_STATUS_FIELD}`;
const CLASSIFICATION_PATH = `${ADDITIONAL_INFORMATION_SECTION}.${CLASSIFICATION_FIELD}`;

interface AditionalInfoSectionProps {
  subCampaignFeedback: SubCampaignFeedback;
  handleChangeText: (label: string, value: string, section: string) => void;
  checkIsDisabledField: (field: string) => boolean;
  getLabelForPossibleDisabledInput: (label: string, field: string) => ReactNode;
  subCampaignForms: ReduxFormsState | null;
  originalValues: OriginalValues;
  setFeedback: (feedback: SubCampaignFeedback) => void;
  index: number;
  classes: any;
  checkDisabled: boolean;
  campaignINTAKTURL: string;
  setURLsNotLoaded: (value: string[]) => void;
}

const AditionalInfoSection: React.FC<AditionalInfoSectionProps> = (props) => {
  const { subCampaignFeedback, handleChangeText, checkIsDisabledField, getLabelForPossibleDisabledInput, subCampaignForms,
    originalValues, setFeedback, index, classes, checkDisabled, campaignINTAKTURL,
    setURLsNotLoaded } = props;

  const [checkZASMCampaignNumberError, setCheckZASMCampaignNumberError] = useState(false);
  const [clickOnIntaktWithSave, setClickOnIntaktWithSave] = useState(false);
  const { t } = useTranslation();
  const lang = useAppLanguage();
  const { handleCriticalError } = useError();
  const { addToast } = useToastContext();
  const checkZASMCampaignExists = useApiRequest(campaignServices.checkZASMCampaignExists);
  const campaign = subCampaignFeedback.campaigns[index];

  const createINKTAKDisabled = (!campaign?.zasmIntaktSynchronization?.zasmId
    && !isValidCampaignNumberFormat(campaign?.campaignManagerEntry.campaignNumber))
  || (campaign?.zasmIntaktSynchronization?.updatedByINTAKT
    && campaign?.zasmIntaktSynchronization?.intaktFieldsSynchronized.includes(CAMPAIGN_STATUS_PATH))
  || checkZASMCampaignNumberError || checkDisabled;

  const stateAdditionalInfo = (subCampaignForms?.stateAdditionalInfo as FeedbackCustomForm)?.options
    ?.find((state) => state.enumAPI === campaign?.additionalInformationAboutTheCase?.[CAMPAIGN_STATUS_FIELD]) || '';

  useEffect(() => {
    setCheckZASMCampaignNumberError(false);
  }, [campaign?.campaignManagerEntry.campaignNumber]);

  const openIntaktCR = () => {
    if (campaign?.zasmIntaktSynchronization?.updatedByZASM) {
      setURLsNotLoaded(openINTAKTWindow(campaignINTAKTURL));
      setCheckZASMCampaignNumberError(false);

      return;
    }
    checkZASMCampaignExists(campaign?.campaignManagerEntry.campaignNumber)
      .then((response) => {
        if (response) {
          setURLsNotLoaded(openINTAKTWindow(campaignINTAKTURL));
          setCheckZASMCampaignNumberError(false);

          return;
        }
        setCheckZASMCampaignNumberError(true);
        addToast(t('errors.ZASMNumberNotExitsts'), 'Error');
      })
      .catch((err) => {
        if (err.code === errorTypes.ZASM_SERVER_ERROR) {
          addToast(t('errors.createZASMCrError'), 'Error');

          return;
        };
        handleCriticalError(err);
      });
  };

  const onClickCreateINTAKTCR = () => {
    if (originalValues?.hasChange) {
      setFeedback({
        ...subCampaignFeedback,
        saveNeeded: true,
      });
      setClickOnIntaktWithSave(true);

      return;
    }
    openIntaktCR();
  };

  useEffect(() => {
    if (!originalValues?.hasChange && clickOnIntaktWithSave && subCampaignFeedback?.saveNeeded) {
      delete subCampaignFeedback.saveNeeded;

      setClickOnIntaktWithSave(false);
      openIntaktCR();
    }
  }, [subCampaignFeedback?.saveNeeded, originalValues?.hasChange, clickOnIntaktWithSave]);

  const classificationAdditionalInfo = (subCampaignForms?.classificationsAdditionalInfo as FeedbackCustomForm)?.options
    ?.find((cls) => cls.enumAPI === campaign?.additionalInformationAboutTheCase?.[CLASSIFICATION_FIELD]?.[0]) || '';

  return (
    <>
      <TitleWithDate title={t('feedback.additionalInformationArea')} date={campaign?.zasmIntaktSynchronization?.lastINTAKTUpdateDate} />
      <div className="w-100" data-testid={`feedback-aditional-info-section-${index}`}>
        <Grid container spacing={2} rowSpacing={1} sx={{ marginBottom: '16px' }} columnSpacing={1}>
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              id={`input-aditional-info-campaign-status-feedback-${index}`}
              key={`campaignStateAdditionalInfoAutocomplete-${lang}`}
              data-testid={`input-aditional-info-campaign-status-feedback-${index}`}
              value={stateAdditionalInfo as unknown as FeedbackCustomFormOption}
              options={(subCampaignForms?.stateAdditionalInfo as FeedbackCustomForm)?.options ?? []}
              onChange={(ev, value) => handleChangeText(CAMPAIGN_STATUS_FIELD, value?.enumAPI ?? '', ADDITIONAL_INFORMATION_SECTION)}
              isOptionEqualToValue={(optionToTest, currentValue) => optionToTest?.enumAPI === currentValue?.enumAPI}
              getOptionLabel={(option) => option?.[lang] ?? ''}
              renderInput={(params) => (
                <HighlightMask
                  fieldId={`input-aditional-info-campaign-status-feedback-${index}-text`}
                  fieldPath={[FEEDBACK_SECTION, `campaigns[${index}]`, ADDITIONAL_INFORMATION_SECTION, CAMPAIGN_STATUS_FIELD]}
                  value={stateAdditionalInfo?.[lang]}
                  fieldType={fieldTypes.DROPDOWN}
                  disabled={checkIsDisabledField(CAMPAIGN_STATUS_PATH)}
                  noTranslationField
                >
                  <TextField
                    {...params}
                    label={getLabelForPossibleDisabledInput('feedback.stateAdditionalInfo', CAMPAIGN_STATUS_PATH)}
                    variant="outlined"
                    disabled={checkIsDisabledField(CAMPAIGN_STATUS_PATH)}
                  />
                </HighlightMask>
              )}
              classes={checkIsDisabledField(CAMPAIGN_STATUS_PATH) ? { inputRoot: classes.disableInputRoot } : {}}
              disabled={checkIsDisabledField(CAMPAIGN_STATUS_PATH)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              id={`input-aditional-info-campaign-classification-feedback-${index}`}
              key={`campaignClassificationAdditionalInfoAutocomplete-${lang}`}
              data-testid={`input-aditional-info-campaign-classification-feedback-${index}`}
              value={classificationAdditionalInfo as unknown as FeedbackCustomFormOption}
              options={(subCampaignForms?.classificationsAdditionalInfo as FeedbackCustomForm)?.options ?? []}
              onChange={(ev, value) => handleChangeText(CLASSIFICATION_FIELD, value?.enumAPI ?? '', ADDITIONAL_INFORMATION_SECTION)}
              isOptionEqualToValue={(optionToTest, currentValue) => optionToTest?.enumAPI === currentValue?.enumAPI}
              getOptionLabel={(option) => option?.[lang] ?? ''}
              renderInput={(params) => (
                <HighlightMask
                  fieldId={`input-aditional-info-campaign-classification-feedback-${index}-text`}
                  fieldPath={[FEEDBACK_SECTION, `campaigns[${index}]`, ADDITIONAL_INFORMATION_SECTION, CLASSIFICATION_FIELD]}
                  value={classificationAdditionalInfo?.[lang]}
                  fieldType={fieldTypes.DROPDOWN}
                  disabled={checkIsDisabledField(CLASSIFICATION_PATH)}
                  noTranslationField
                >
                  <TextField
                    {...params}
                    label={getLabelForPossibleDisabledInput('feedback.classification', CLASSIFICATION_PATH)}
                    variant="outlined"
                    disabled={checkIsDisabledField(CLASSIFICATION_PATH)}
                  />
                </HighlightMask>
              )}
              classes={checkIsDisabledField(CLASSIFICATION_PATH) ? { inputRoot: classes.disableInputRoot } : {}}
              disabled={checkIsDisabledField(CLASSIFICATION_PATH)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {!checkDisabled && (
              <Grid container xs={12} direction="column" justifyContent="center" alignItems="center" gap={1}>
                <Button
                  id="button_create_INTAKT_campaign"
                  handleClick={onClickCreateINTAKTCR}
                  buttonStyle="PrimaryRound"
                  btnText={t('feedback.createINTAKTCrButton')}
                  disabled={createINKTAKDisabled}
                />
              </Grid>
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={3} />
        </Grid>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  subCampaignFeedback: getSubCampaignFeedback(state),
  originalValues: getSubCampaignOriginalValues(state),
});

const actionCreators = {
  setFeedback: setSubCampaignFeedback,
};

export default (connect(mapStateToProps, actionCreators)(AditionalInfoSection));
